import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { BookOpen, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { DerivedSeries } from '@/hooks/useSeriesProgress';

interface FindNextBookButtonProps {
  series: DerivedSeries;
}

export function FindNextBookButton({ series }: FindNextBookButtonProps) {
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const nextPosition = series.highestPositionRead + 1;

  const handleFindNextBook = async () => {
    setSearching(true);
    try {
      // Search for the next book in the series
      const { data: books, error } = await supabase
        .from('books')
        .select('id, title, series_position, cover_url')
        .ilike('series_name', series.seriesName)
        .eq('series_position', nextPosition)
        .limit(5);

      if (error) throw error;

      if (books && books.length > 0) {
        navigate(`/book/${books[0].id}`);
        toast.success(`Found: ${books[0].title}`);
      } else {
        toast.info('Book not in database. Searching...');
        navigate(`/search?q=${encodeURIComponent(`${series.seriesNameDisplay} book ${nextPosition} ${series.authorName}`)}`);
      }
    } catch (error) {
      console.error('Error finding next book:', error);
      toast.error('Could not find next book');
    } finally {
      setSearching(false);
    }
  };

  // Don't show button if series is complete
  if (series.totalKnown && series.positionsRead.length >= series.totalKnown) {
    return null;
  }

  return (
    <Button
      onClick={handleFindNextBook}
      disabled={searching}
      variant="outline"
      size="sm"
      className="w-full"
    >
      {searching ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Searching...
        </>
      ) : (
        <>
          <BookOpen className="h-4 w-4 mr-2" />
          Find Next Book (Book {nextPosition})
        </>
      )}
    </Button>
  );
}
